import React, { useState } from 'react';
import ReactGA from 'react-ga';
import styles from './index.module.scss';
import logo from './logo.png';

const EmployerCostCalculator = () => {
  const [salary, setSalary] = useState(30000); // Default annual salary
  const [contractLength, setContractLength] = useState(1); // Default contract length
  const [country, setCountry] = useState('italy'); // Default country
  const [includeHealthInsurance, setIncludeHealthInsurance] = useState(false);
  const [includeThirteenthMonth, setIncludeThirteenthMonth] = useState(true); // For France
  const [includePension, setIncludePension] = useState(false); // For Ireland
  const [include401k, setInclude401k] = useState(false); // For USA
  const [selectedCurrency, setSelectedCurrency] = useState('€')

  // Italy employer cost calculator
  const italyEmployerCostCalculator = (annualSalary, contractLength = 1) => {
    const employerSocialSecurityRate = 0.32;
    const tfrRate = 1 / 13.5;
    const thirteenthMonthRate = 1 / 12;
    const insuranceAndBenefits = 1000;

    const totalGrossSalary = annualSalary * contractLength;
    const employerSocialSecurity = totalGrossSalary * employerSocialSecurityRate;
    const tfrAccrual = totalGrossSalary * tfrRate;
    const thirteenthMonthSalary = totalGrossSalary * thirteenthMonthRate;
    const miscExpenses = insuranceAndBenefits * contractLength;

    const onboarding = 2000;
    const recruitment = 7000;
    const training = 4000;

    const totalEmployerCost =
      totalGrossSalary +
      employerSocialSecurity +
      tfrAccrual +
      thirteenthMonthSalary +
      miscExpenses +
      onboarding +
      recruitment +
      training;

    return {
      totalGrossSalary,
      employerSocialSecurity,
      tfrAccrual,
      thirteenthMonthSalary,
      miscExpenses,
      totalEmployerCost,
      onboarding,
      recruitment,
      training
    };
  };

  // France employer cost calculator
  const franceEmployerCostCalculator = (annualSalary, contractLength = 1, includeThirteenthMonth = true) => {
    const employerSocialSecurityRate = 0.42;
    const severanceRatePerYear = 1 / 4;
    const mealVoucherPerDay = 5;
    const workdaysPerYear = 220;
    const transportationAllowance = 500;
    const healthInsuranceContribution = 500;
    const trainingContributionRate = 0.01;

    const totalGrossSalary = annualSalary * contractLength;
    const employerSocialSecurity = totalGrossSalary * employerSocialSecurityRate;
    const severancePay = (annualSalary / 12) * severanceRatePerYear * contractLength;
    const thirteenthMonthSalary = includeThirteenthMonth ? (annualSalary / 12) * contractLength : 0;
    const mealVouchersCost = mealVoucherPerDay * workdaysPerYear * contractLength;
    const totalTransportationAllowance = transportationAllowance * contractLength;
    const totalHealthInsurance = healthInsuranceContribution * contractLength;
    const trainingContribution = totalGrossSalary * trainingContributionRate;

    const onboarding = 2000;
    const recruitment = 6000;
    const training = 4000;

    const totalEmployerCost =
      totalGrossSalary +
      employerSocialSecurity +
      severancePay +
      thirteenthMonthSalary +
      mealVouchersCost +
      totalTransportationAllowance +
      totalHealthInsurance +
      trainingContribution +
      onboarding +
      recruitment +
      training;

    return {
      totalGrossSalary,
      employerSocialSecurity,
      severancePay,
      thirteenthMonthSalary,
      mealVouchersCost,
      totalTransportationAllowance,
      totalHealthInsurance,
      trainingContribution,
      totalEmployerCost,
      onboarding,
      recruitment,
      training
    };
  };

  // UK employer cost calculator
  const ukEmployerCostCalculator = (annualSalary, contractLength = 1, includeHealthInsurance = false) => {
    const employerNicRate = 0.138;
    const nicThreshold = 9100;
    const pensionContributionRate = 0.03;
    const pensionThreshold = 6240;
    const weeklyPayCap = 643;
    const privateHealthInsuranceCost = 1000;
    const apprenticeshipLevyRate = 0.005;

    const totalGrossSalary = annualSalary * contractLength;
    const nicEarnings = Math.max(0, annualSalary - nicThreshold);
    const employerNic = nicEarnings * employerNicRate * contractLength;
    const pensionEarnings = Math.max(0, annualSalary - pensionThreshold);
    const employerPension = pensionEarnings * pensionContributionRate * contractLength;
    const yearsOfService = 5;
    const weeklySalary = annualSalary / 52;
    const healthInsurance = includeHealthInsurance ? privateHealthInsuranceCost * contractLength : 0;
    const apprenticeshipLevy = totalGrossSalary * apprenticeshipLevyRate;
    const severanceWeeklyPay = Math.min(weeklySalary, weeklyPayCap)
    var severancePay = severanceWeeklyPay * yearsOfService

    if (contractLength < 2) {
      severancePay = 0
    }

    const onboarding = 3000;
    const recruitment = 8000;
    const training = 4000;

    const totalEmployerCost =
      totalGrossSalary +
      employerNic +
      employerPension +
      severancePay +
      healthInsurance +
      apprenticeshipLevy +
      onboarding +
      recruitment +
      training;

    return {
      totalGrossSalary,
      employerNic,
      employerPension,
      severancePay,
      healthInsurance,
      apprenticeshipLevy,
      totalEmployerCost,
      onboarding,
      recruitment,
      training
    };
  };

  // USA employer cost calculator
  // const usaEmployerCostCalculator = (
  //   annualSalary,
  //   contractLength = 1,
  //   includeHealthInsurance = false,
  //   include401k = false
  // ) => {
  //   const socialSecurityRate = 0.062;
  //   const socialSecurityWageBase = 160200;
  //   const medicareRate = 0.0145;
  //   const futaRate = 0.006;
  //   const futaWageBase = 7000;
  //   const sutaRate = 0.03;
  //   const sutaWageBase = 15000;
  //   const privateHealthInsuranceCost = 7000;
  //   const employer401kMatchRate = 0.05;

  //   const totalGrossSalary = annualSalary * contractLength;
  //   const socialSecurity = Math.min(annualSalary, socialSecurityWageBase) * socialSecurityRate * contractLength;
  //   const medicare = annualSalary * medicareRate * contractLength;
  //   const futaTax = Math.min(annualSalary, futaWageBase) * futaRate * contractLength;
  //   const sutaTax = Math.min(annualSalary, sutaWageBase) * sutaRate * contractLength;
  //   const yearsOfService = 5;
  //   const weeklySalary = annualSalary / 52;
  //   const severancePay = weeklySalary * 2 * yearsOfService;
  //   const healthInsurance = includeHealthInsurance ? privateHealthInsuranceCost * contractLength : 0;
  //   const employer401kMatch = include401k ? annualSalary * employer401kMatchRate * contractLength : 0;

  //   const totalEmployerCost =
  //     totalGrossSalary +
  //     socialSecurity +
  //     medicare +
  //     futaTax +
  //     sutaTax +
  //     severancePay +
  //     healthInsurance +
  //     employer401kMatch;

  //   return {
  //     totalGrossSalary,
  //     socialSecurity,
  //     medicare,
  //     futaTax,
  //     sutaTax,
  //     severancePay,
  //     healthInsurance,
  //     employer401kMatch,
  //     totalEmployerCost,
  //   };
  // };

  // Ireland employer cost calculator
  const irelandEmployerCostCalculator = (
    annualSalary,
    contractLength = 1,
    includeHealthInsurance = false,
    includePension = false
  ) => {
    const employerPrsiRate = 0.112;
    const healthInsuranceCost = 3000;
    const pensionContributionRate = 0.05;
    const redundancyRate = 2;

    const totalGrossSalary = annualSalary * contractLength;
    const employerPrsi = annualSalary * employerPrsiRate * contractLength;
    const employerPension = includePension ? annualSalary * pensionContributionRate * contractLength : 0;
    const yearsOfService = 5;
    const weeklySalary = annualSalary / 52;
    const severancePay = weeklySalary * redundancyRate * yearsOfService;
    const healthInsurance = includeHealthInsurance ? healthInsuranceCost * contractLength : 0;

    const onboarding = 3000;
    const recruitment = 7000;
    const training = 5000;

    const totalEmployerCost =
      totalGrossSalary +
      employerPrsi +
      employerPension +
      severancePay +
      healthInsurance +
      onboarding +
      recruitment +
      training;

    return {
      totalGrossSalary,
      employerPrsi,
      employerPension,
      severancePay,
      healthInsurance,
      totalEmployerCost,
      onboarding,
      recruitment,
      training
    };
  };

  const formattedNumber = (number) => {
    return number.toLocaleString('en-US');
  };

  // Handle country change
  const handleCountryChange = (event) => {
    var currency = '€'
    if (event.target.value === "uk") {
      currency = '£'
    } else if (event.target.value === "usa") {
      currency = '$'
    }

    ReactGA.event({
      category: 'Country Change',
      action: 'Country change value to: ' + event.target.value,
      label: 'Country Change',
    });

    setSelectedCurrency(currency)
    setCountry(event.target.value);
  };

  // Handle salary change
  const handleSalaryChange = (event) => {
    ReactGA.event({
      category: 'Salary Change',
      action: 'Changed salary value to: ' + event.target.value,
      label: 'Salary Change',
    });

    setSalary(event.target.value);
  };

  // Handle contract length change
  const handleContractLengthChange = (event) => {
    ReactGA.event({
      category: 'Contract Length Change',
      action: 'Changed contract length to: ' + event.target.value,
      label: 'Contract Length',
    });

    setContractLength(event.target.value);
  }; 

  // Calculate costs based on selected country
  let costs;
  switch (country) {
    case 'italy':
      costs = italyEmployerCostCalculator(salary, contractLength);
      break;
    case 'france':
      costs = franceEmployerCostCalculator(salary, contractLength, includeThirteenthMonth);
      break;
    case 'uk':
      costs = ukEmployerCostCalculator(salary, contractLength, includeHealthInsurance);
      break;
    // case 'usa':
    //   costs = usaEmployerCostCalculator(salary, contractLength, includeHealthInsurance, include401k);
    //   break;
    case 'ireland':
      costs = irelandEmployerCostCalculator(salary, contractLength, includeHealthInsurance, includePension);
      break;
    default:
      costs = italyEmployerCostCalculator(salary, contractLength);
      break;
  }

  const formatKey = (key) => {
    return key
      .replace(/([A-Z])/g, ' $1')  // Add spaces before capital letters
      .replace(/^./, str => str.toUpperCase())  // Capitalize the first letter of the string
      .replace(/\b\w/g, str => str.toUpperCase()); // Capitalize the first letter of each word
  };

  const scheduleCall = () => {
    ReactGA.event({
      category: 'Schedule Call',
      action: 'Clicked Schedule ',
      label: 'Clicked Schedule Call',
    });
    window.open("https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ2aSvcQ0Ca5N0kJZai1yCZ6Jhdhfkw1Md3PNJCR1nBWL7RD6m2NWtY9opkcv7wH4-Y7oO5GD0DR", "_blank")
  }

  return (
    <div className={styles.eccWidget}>
      <div className={styles.logo}>
        <a href='https://mydevconnect.com' target='_blank' rel='noreferrer'><img src={logo} alt='' /></a>
      </div>
      <h2 className={styles.title}><span className={styles.orange}>MyDevConnect</span> vs Standard Hiring</h2>

      <table>
        <tr>
          <td>Select Country</td>
          <td>
            <select value={country} onChange={handleCountryChange}>
              <option value="italy">Italy (€)</option>
              <option value="france">France (€)</option>
              <option value="uk">United Kingdom (£)</option>
              <option value="ireland">Ireland (€)</option>
            </select>
          </td>
        </tr>
        <tr>
          <td>Annual Salary of <span>{selectedCurrency}{formattedNumber(salary)}</span></td>
          <td>
            <input
              type="range"
              min="20000"
              max="100000"
              step="1000"
              value={salary}
              onChange={handleSalaryChange}
            />
          </td>
        </tr>
        <tr>
          <td>Contract Length (years)</td>
          <td>
            <input
              type="number"
              min="1"
              max="10"
              value={contractLength}
              onChange={handleContractLengthChange}
            />
          </td>
        </tr>
        {country === 'france' && (
          <tr>
            <td>Include 13th Month Salary</td>
            <td>
              <input
                type="checkbox"
                checked={includeThirteenthMonth}
                onChange={(e) => setIncludeThirteenthMonth(e.target.checked)}
              />
            </td>
          </tr>
        )}
        {(country === 'uk' || country === 'usa' || country === 'ireland') && (
          <tr>
            <td>
              Include Health Insurance
            </td>
            <td>
              <input
                type="checkbox"
                checked={includeHealthInsurance}
                onChange={(e) => setIncludeHealthInsurance(e.target.checked)}
              />
            </td>
          </tr>
        )}

        {country === 'ireland' && (
          <tr>
            <td>
              Include Pension Contribution
            </td>
            <td>
              <input
                type="checkbox"
                checked={includePension}
                onChange={(e) => setIncludePension(e.target.checked)}
              />
            </td>
          </tr>
        )}

        {country === 'usa' && (
          <tr>
            <td>
              Include 401k Contribution
            </td>
            <td>
              <input
                type="checkbox"
                checked={include401k}
                onChange={(e) => setInclude401k(e.target.checked)}
              />
            </td>
          </tr>
        )}
      </table>


      <div className={styles.container}>
        <div className={styles.mdcContainer}>
          <h3>MyDevConnect Hiring</h3>
          <table>
            <tr>
              <td>Total Gross Salary</td>
              <td>{selectedCurrency}{formattedNumber(costs.totalGrossSalary)}</td>
            </tr>
            {Object.keys(costs).map((key, index) => {
              if (key !== 'totalGrossSalary' && key !== 'totalEmployerCost') {
                return (
                  <tr key={index}>
                    <td>
                      {formatKey(key)}
                    </td>
                    <td>
                      {selectedCurrency}{0}
                    </td>
                  </tr>
                );
              }
              return null;
            })}
          </table>
          <h3>{selectedCurrency}{formattedNumber(costs.totalGrossSalary)}</h3>
        </div>
        <div className={styles.marketContainer}>
          <h3>Standard Hiring</h3>
          <table>
            <tr>
              <td>Total Gross Salary</td>
              <td>{selectedCurrency}{formattedNumber(costs.totalGrossSalary)}</td>
            </tr>
            {Object.keys(costs).map((key, index) => {
              if (key !== 'totalGrossSalary' && key !== 'totalEmployerCost') {
                return (
                  <tr key={index}>
                    <td>
                      {formatKey(key)}
                    </td>
                    <td>
                      {selectedCurrency}{formattedNumber(costs[key])}
                    </td>
                  </tr>
                );
              }
              return null;
            })}
          </table>
          <h3>{selectedCurrency}{formattedNumber(costs.totalEmployerCost)}</h3>
        </div>
      </div>
      <div className={styles.summaryContainer}>
            <p>Interested in learning more?</p>
            <button onClick={scheduleCall}>Schedule a call</button>
      </div>
    </div>
  );
};

export default EmployerCostCalculator;
